import { Navigate, useParams } from "react-router-dom";
import { useAccess } from "../../api/hooks";
import { LEGACY_TABS, moduleById, modulePath } from "../../lib/modules";
import { AlarmsTab } from "../customer/AlarmsTab";
import { AuditTab } from "../customer/AuditTab";
import { ConcurrencyTab } from "../customer/ConcurrencyTab";
import { PathsView } from "../customer/JourneysTab";
import { ContactsSection } from "../journeys/ContactsSection";
import { OverviewSection } from "../journeys/OverviewSection";
import { RewindSection } from "../journeys/RewindSection";
import { QuotasTab } from "../customer/QuotasTab";
import { SupportCasesTab } from "../customer/SupportCasesTab";
import { TrustedAdvisorTab } from "../customer/TrustedAdvisorTab";
import { ModulePage, PlannedSection } from "./ModulePage";

export function AlarmsPage() {
  return <ModulePage module="alarms">{(_d, environmentId) => <AlarmsTab environmentId={environmentId} />}</ModulePage>;
}

export function QuotasPage() {
  const access = useAccess();
  return (
    <ModulePage module="quotas">
      {/* ADR-0017: `quotas:request` is the permission; the stack's opt-in role is what makes it possible. */}
      {(d, environmentId) => <QuotasTab environmentId={environmentId} canRequestIncrease={access.can(environmentId, "quotas:request") && d.enabledComponents.includes("QuotaIncreaseRequestRole")} connectedAt={d.customer.connectivity_verified_at} />}
    </ModulePage>
  );
}

export function TrustedAdvisorEnvironmentPage() {
  return <ModulePage module="trusted-advisor">{(d, environmentId) => <TrustedAdvisorTab environmentId={environmentId} available={d.trustedAdvisorAvailable} connectedAt={d.customer.connectivity_verified_at} />}</ModulePage>;
}

export function SupportPage() {
  return <ModulePage module="support">{(d, environmentId) => <SupportCasesTab environmentId={environmentId} connectedAt={d.customer.connectivity_verified_at} />}</ModulePage>;
}

export function AuditPage() {
  return <ModulePage module="audit">{(_d, environmentId) => <AuditTab environmentId={environmentId} />}</ModulePage>;
}

const JOURNEY_TITLES: Record<string, string> = { overview: "Journeys · Overview", contacts: "Journeys · Contacts", paths: "Journeys · Paths", concurrency: "Journeys · Concurrency", rewind: "Journeys · Rewind", observability: "Journeys · Observability" };

export function JourneysPage() {
  const access = useAccess();
  const { section = "overview" } = useParams();
  const title = JOURNEY_TITLES[section];
  if (!title) return <Navigate to=".." replace />;
  return (
    <ModulePage module="journeys" section={section} title={title}>
      {(d, environmentId) => {
        // ADR-0017: `journeys.contacts:inspect` opens a trace; the stack's opt-in role is what makes it possible.
        const canInspect = access.can(environmentId, "journeys.contacts:inspect") && d.enabledComponents.includes("JourneyInspectorRole");
        switch (section) {
          case "paths":
            // #348: always the view; an empty one says which precondition is missing rather than guessing from the config.
            return <PathsView environmentId={environmentId} canInspect={canInspect} detail={d} />;
          case "contacts":
            return <ContactsSection environmentId={environmentId} canInspect={canInspect} detail={d} />;
          case "concurrency":
            return <ConcurrencyTab environmentId={environmentId} connectedAt={d.customer.connectivity_verified_at} />;
          case "rewind":
            // ADR-0016: the agents at a minute are the environment's managers' and Gairm staff's (the owner, #231).
            return <RewindSection environmentId={environmentId} canInspect={canInspect} canSeeAgents={access.can(environmentId, "journeys.agents:view")} />;
          case "observability":
            return (
              <PlannedSection title="Observability - audio, network and the agent's desktop">
                Per-second audio and network quality from the agent browser extension, joined to each contact, with the moment a call went wrong marked on its trace (ADR-0015 section 11). Lands with the extension.
              </PlannedSection>
            );
          default:
            return <OverviewSection environmentId={environmentId} envName={d.customer.friendly_name} canInspect={canInspect} />;
        }
      }}
    </ModulePage>
  );
}

/** `/customers/:environmentId/:tab` (the old drill-down) lands on the module it became. */
export function LegacyTabRedirect() {
  const { environmentId = "", tab = "" } = useParams();
  const target = LEGACY_TABS[tab];
  if (!target) return <Navigate to={`/customers/${environmentId}`} replace />;
  return <Navigate to={modulePath(moduleById(target.module), environmentId, target.section)} replace />;
}

export function LegacyContactRedirect() {
  const { environmentId = "", contactId = "" } = useParams();
  return <Navigate to={`/journeys/${environmentId}/contacts/${contactId}`} replace />;
}
